import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { MessageCircle, Instagram, Facebook, Sparkles } from 'lucide-react';
import { initPixel, trackWhatsAppClick } from './lib/pixel';
import { LegalModal, LegalDocument } from './components/LegalModal';
import { termsContent, privacyContent } from './components/legalContent';
import { TestimonialsCarousel, Testimonial } from './components/TestimonialsCarousel';
import { ImageCarousel } from './components/ImageCarousel';
import { FloatingWhatsApp } from './components/FloatingWhatsApp';
export function App() {
  const [activeLegalDoc, setActiveLegalDoc] = useState<LegalDocument | null>(null);
  useEffect(() => {
    initPixel();
  }, []);
  const waLink = 'https://wa.me/59995249641';
  const igLink = 'https://www.instagram.com/dushitarot?igsh=NTBzaXM4enhudzZ5&utm_source=qr';
  const fbLink = 'https://www.facebook.com/share/1BTVjr78kF/?mibextid=wwXIfr';
  const testimonials: Testimonial[] = [{
    quote: "Ricardo a primintí tur kos. Den un siman mi a bolbe ku mi pareha. Mi ta rekomend'é 100%.",
    name: 'M.G.',
    photo: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?auto=format&fit=crop&w=200&q=80'
  }, {
    quote: 'E tin un don berdadero. Su lektura tabata kla, honesto i masha efektivo.',
    name: 'J.A.',
    photo: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&w=200&q=80'
  }, {
    quote: 'E sa loke e ta hasi. El a duna mi pas i kontesta berdadero. Danki.',
    name: 'L.R.',
    photo: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?auto=format&fit=crop&w=200&q=80'
  }];
  const pains = [{
    emoji: '💔',
    text: 'Problemanan di pareha òf separashon'
  }, {
    emoji: '😔',
    text: 'Ansiedat, tristesa òf preokupashon'
  }, {
    emoji: '💰',
    text: 'Plaka stanka òf falta di prosperidat'
  }, {
    emoji: '🔮',
    text: 'Sensashon di blokeo òf mala suerte'
  }];
  const galleryImages = ['https://cdn.magicpatterns.com/uploads/9RZQKCWGoYroLEiKwybKmk/ChatGPT_Image_11_jun_2026,_13_12_09.png', 'https://cdn.magicpatterns.com/uploads/cMvzEazCNnQJ7uU4vD5kuY/ChatGPT_Image_11_jun_2026,_13_05_04.png', 'https://cdn.magicpatterns.com/uploads/cvJjKEPj4Lj3E1ot7KdzYt/ChatGPT_Image_11_jun_2026,_13_02_45.png', 'https://cdn.magicpatterns.com/uploads/tUZnmzRCHBoeDqnCrrXxq1/ChatGPT_Image_11_jun_2026,_11_15_26.png'];
  return <div className="min-h-screen w-full bg-brand-bg font-sans flex flex-col items-center justify-center relative overflow-hidden selection:bg-brand-gold selection:text-brand-bg" data-id="element-0">
      {/* Background image */}
      <div className="absolute inset-0 bg-cover bg-center pointer-events-none" style={{
      backgroundImage: "url('https://cdn.magicpatterns.com/uploads/dFhXHTQUuWXbfx4Z8rCD2B/ChatGPT_Image_11_jun_2026,_11_15_26.png')"
    }} aria-hidden="true" data-id="element-1" />
      {/* Dark overlay for readability */}
      <div className="absolute inset-0 bg-brand-bg/85 pointer-events-none" aria-hidden="true" data-id="element-2" />

      {/* Background glow effects */}
      <div className="absolute top-[-20%] left-[-10%] w-[70%] h-[70%] rounded-full bg-brand-gold/5 blur-[120px] pointer-events-none" data-id="element-3" />
      <div className="absolute bottom-[-20%] right-[-10%] w-[70%] h-[70%] rounded-full bg-brand-gold/5 blur-[120px] pointer-events-none" data-id="element-4" />

      {/* Main Card Container */}
      <div className="w-full max-w-4xl px-6 py-12 md:py-16 flex flex-col items-center text-center z-10 animate-fade-up opacity-0" style={{
      animationDelay: '0.2s'
    }} data-id="element-5">
        {/* Icon / Top Accent */}
        <div className="mb-6 md:mb-8 text-brand-gold animate-scale-in opacity-0" style={{
        animationDelay: '0.6s'
      }} data-id="element-6">
          <Sparkles size={40} strokeWidth={1.5} data-id="element-7" />
        </div>

        {/* Title */}
        <h1 className="font-serif text-4xl md:text-6xl lg:text-7xl font-semibold tracking-wide text-white mb-6 md:mb-8 drop-shadow-2xl leading-tight max-w-4xl uppercase" data-id="element-8">
          Konsulta Spiritual{' '}
          <span className="text-brand-gold italic font-light" data-id="element-9">Gratis</span>
        </h1>

        {/* Subtitle */}
        <p className="text-lg md:text-2xl text-gray-200 font-light leading-relaxed max-w-2xl mx-auto mb-10 md:mb-12" data-id="element-10">
          Deskubrí kiko ta blokia bo amor, bo plaka òf bo trankilidat.
        </p>

        {/* Pain points (Dolores) */}
        <div className="w-full max-w-xl mx-auto grid grid-cols-1 sm:grid-cols-2 gap-4 mb-12 md:mb-16" data-id="element-11">
          {pains.map(p => <div key={p.text} className="flex items-center gap-4 text-left bg-white/5 border border-brand-gold/15 rounded-xl px-5 py-4 backdrop-blur-sm" data-id="element-12">
              <span className="text-2xl md:text-3xl shrink-0" aria-hidden="true" data-id="element-13">
                {p.emoji}
              </span>
              <span className="text-base md:text-lg text-gray-200 font-light leading-snug" data-id="element-14">
                {p.text}
              </span>
            </div>)}
        </div>

        {/* Image Carousel (auto-scrolling gallery) */}
        <div className="w-full mb-12 md:mb-16 -mx-6 px-0 md:mx-0" data-id="element-15">
          <ImageCarousel images={galleryImages} data-id="element-16" />
        </div>

        {/* CTA line */}
        <p className="text-base md:text-xl text-brand-gold/90 font-light leading-relaxed max-w-2xl mx-auto mb-8 md:mb-10" data-id="element-17">
          📲 Papia awor ku Ricardo via WhatsApp i risibí bo orientashon inicial
          gratis.
        </p>

        {/* Reader / Ricardo */}
        <motion.div className="mb-12 md:mb-16 flex flex-col items-center relative" initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.7,
        ease: 'easeOut'
      }} data-id="element-18">
          {/* Soft pulsing glow behind the name */}
          <motion.div aria-hidden="true" className="absolute top-6 md:top-8 w-48 h-24 rounded-full bg-brand-gold/20 blur-3xl pointer-events-none" animate={{
          opacity: [0.3, 0.7, 0.3],
          scale: [0.9, 1.1, 0.9]
        }} transition={{
          duration: 3.5,
          repeat: Infinity,
          ease: 'easeInOut'
        }} data-id="element-19" />

          <p className="text-base md:text-lg uppercase tracking-[0.3em] text-gray-400 mb-2 relative z-10" data-id="element-20">
            Vidente
          </p>
          <motion.p className="font-serif text-3xl md:text-5xl text-brand-gold mb-3 relative z-10" animate={{
          scale: [1, 1.04, 1],
          textShadow: ['0 0 0px rgba(200,150,46,0)', '0 0 22px rgba(200,150,46,0.6)', '0 0 0px rgba(200,150,46,0)']
        }} transition={{
          duration: 3.5,
          repeat: Infinity,
          ease: 'easeInOut'
        }} data-id="element-21">
            Ricardo
          </motion.p>
          <p className="text-lg md:text-xl text-gray-200 font-light max-w-xl leading-relaxed relative z-10" data-id="element-22">
            25 aña di eksperensia den unionnan di pareha
          </p>
        </motion.div>

        {/* CTA */}
        <div className="w-full flex justify-center items-center mb-16 md:mb-20" data-id="element-23">
          <a href={waLink} target="_blank" rel="noopener noreferrer" onClick={() => trackWhatsAppClick('pap')} className="group w-full sm:w-auto flex items-center justify-center gap-3 bg-brand-gold hover:bg-brand-goldHover text-brand-bg px-10 md:px-12 py-4 md:py-5 rounded-full font-medium text-lg md:text-2xl transition-all duration-300 shadow-[0_0_40px_rgba(200,150,46,0.25)] hover:shadow-[0_0_60px_rgba(200,150,46,0.4)] hover:-translate-y-1" data-id="element-24">
            <MessageCircle size={28} className="group-hover:scale-110 transition-transform duration-300" data-id="element-25" />
            <span data-id="element-26">👉 Mi ke mi konsulta gratis</span>
          </a>
        </div>

        {/* Testimonials Carousel */}
        <div className="w-full mb-16 md:mb-20" data-id="element-27">
          <TestimonialsCarousel testimonials={testimonials} data-id="element-28" />
        </div>

        {/* Footer / Socials */}
        <div className="flex flex-col items-center gap-6 md:gap-8 mt-auto w-full" data-id="element-29">
          <div className="flex gap-8" data-id="element-30">
            <a href={igLink} target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-brand-gold transition-colors duration-300 p-2 hover:scale-110 transform" aria-label="Instagram" data-id="element-31">
              <Instagram size={32} strokeWidth={1.5} data-id="element-32" />
            </a>
            <a href={fbLink} target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-brand-gold transition-colors duration-300 p-2 hover:scale-110 transform" aria-label="Facebook" data-id="element-33">
              <Facebook size={32} strokeWidth={1.5} data-id="element-34" />
            </a>
          </div>

          <div className="flex flex-col items-center gap-4" data-id="element-35">
            <div className="flex items-center gap-3 text-gray-600" data-id="element-36">
              <div className="w-1 h-1 rounded-full bg-brand-gold/50" data-id="element-37" />
              <p className="text-sm font-light tracking-[0.3em]" data-id="element-38">CURAÇAO</p>
              <div className="w-1 h-1 rounded-full bg-brand-gold/50" data-id="element-39" />
            </div>

            <div className="flex items-center gap-4 text-xs text-gray-500 font-light" data-id="element-40">
              <button onClick={() => setActiveLegalDoc(termsContent)} className="hover:text-brand-gold transition-colors" data-id="element-41">
                Términos y Condiciones
              </button>
              <span data-id="element-42">|</span>
              <button onClick={() => setActiveLegalDoc(privacyContent)} className="hover:text-brand-gold transition-colors" data-id="element-43">
                Política de Privacidad
              </button>
            </div>
          </div>
        </div>
      </div>

      <LegalModal isOpen={!!activeLegalDoc} onClose={() => setActiveLegalDoc(null)} document={activeLegalDoc} data-id="element-44" />

      <FloatingWhatsApp href={waLink} onClick={() => trackWhatsAppClick('pap')} data-id="element-45" />
    </div>;
}